import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

function Page(props: { children: ReactNode | React.JSX.Element }) {
  return <div className="tw-text-center tw-px-6 tw-pb-6">{props.children}</div>;
}

function Title(props: { children: ReactNode }) {
  return (
    <h2 className={`tw-font-['GTUltraFine'] tw-text-primary tw-text-3xl tw-font-medium tw-p-0 tw-m-0 lg:tw-text-4xl`}>
      {props.children}
    </h2>
  );
}

function Text(props: { children: ReactNode }) {
  return <p className="tw-text-black tw-font-normal tw-m-0 tw-p-0 tw-text-lg">{props.children}</p>;
}

function DateText(props: { children: ReactNode }) {
  return <p className="tw-text-left tw-text-[#2c5466] tw-font-bold lg:tw-text-base tw-pb-2">{props.children}</p>;
}

function ErrorText(props: { children: ReactNode }) {
  return (
    <p
      style={{ marginTop: '1rem' }}
      className="tw-text-black tw-font-['BentonSans'] tw-p-2 tw-text-sm tw-font-medium tw-bg-[#F3E8DF]"
    >
      {props.children}
    </p>
  );
}

function UploadBox(props: { children: ReactNode; whiteBackground?: boolean }) {
  return (
    <div
      className={`lg:tw-p-4 lg:tw-py-12 lg:tw-mt-4 lg:tw-gap-x-10
    lg:tw-m-auto lg:tw-max-w-screen-md ${props.whiteBackground ? '' : 'lg:tw-bg-[#F9F5F2]'}
    lg:tw-grid lg:tw-grid-rows-1 lg:tw-grid-flow-col lg:tw-auto-cols-fr`}
    >
      {props.children}
    </div>
  );
}

function PhotosBox(props: { children: ReactNode; whiteBackground?: boolean }) {
  return (
    <div
      className={`lg:tw-p-4 lg:tw-py-12 lg:tw-gap-x-10
    lg:tw-m-auto lg:tw-max-w-screen-md ${props.whiteBackground ? '' : 'lg:tw-bg-[#F9F5F2]'}
    lg:tw-grid lg:tw-grid-rows-1 lg:tw-grid-flow-col lg:tw-auto-cols-fr`}
    >
      {props.children}
    </div>
  );
}

function TakePictureBox(props: { children: ReactNode }) {
  return (
    <div
      className={`lg:tw-p-4 lg:tw-gap-x-10
    lg:tw-m-auto lg:tw-max-w-screen-md
    lg:tw-grid lg:tw-grid-rows-1 lg:tw-grid-flow-col lg:tw-auto-cols-fr`}
    >
      {props.children}
    </div>
  );
}

function UploadBoxImage(props: { children: ReactNode }) {
  return <div className="tw-py-8 lg:tw-row-span-2 lg:tw-py-0">{props.children}</div>;
}

function UploadBoxContainer(props: { children: ReactNode }) {
  return (
    <div className="lg:tw-text-left lg:tw-col-span-1 lg:tw-py-0 lg:tw-pt-8 lg:tw-flex lg:tw-flex-col">
      {props.children}
    </div>
  );
}

function TakePictureContainer(props: { children: ReactNode }) {
  return (
    <div
      className="lg:tw-text-left lg:tw-col-span-1 lg:tw-py-0 lg:tw-flex lg:tw-flex-col 
    lg:tw-justify-center"
    >
      {props.children}
    </div>
  );
}

function ContainerTitle(props: { children: ReactNode }) {
  return <h3 className={`tw-text-black tw-text-2xl lg:tw-text-xl tw-font-bold tw-p-0 tw-m-0`}>{props.children}</h3>;
}

function ContainerText(props: { children: ReactNode }) {
  return (
    <p className="tw-text-black tw-font-normal tw-m-0 tw-p-0 tw-text-lg lg:tw-text-sm lg:tw-max-w-72">
      {props.children}
    </p>
  );
}

function ContainerList(props: { children: ReactNode }) {
  return (
    <ul
      className="tw-list-none [&_li]:tw-pt-1 tw-text-black 
    tw-font-normal tw-m-0 tw-p-0 tw-text-lg lg:tw-text-sm lg:tw-max-w-64"
    >
      {props.children}
    </ul>
  );
}

function ContainerListItem(props: { children: ReactNode }) {
  return (
    <li className=" tw-text-black tw-font-normal tw-m-0 tw-p-0 tw-text-lg lg:tw-text-sm lg:tw-max-w-64">
      {props.children}
    </li>
  );
}

function ContainerAction(props: { children: ReactNode }) {
  return (
    <div
      className={`tw-flex tw-w-full tw-justify-center lg:tw-justify-start  tw-gap-4 tw-pt-4
            [&_*]:tw-text-sm [&_*]:lg:tw-text-xs [&_*]:tw-font-semibold
            [&_*]:tw-tracking-tighter [&_*]:tw-no-underline [&_*]:tw-uppercase
            [&_*]:tw-hover:tw-no-underline
            [&_*]:tw-rounded-full [&_*]:tw-min-w-36 
            [&_*]:tw-flex [&_*]:tw-justify-center
            `}
    >
      {props.children}
    </div>
  );
}

function TakePictureContainerAction(props: { children: ReactNode }) {
  return (
    <div
      className={`tw-flex tw-w-full tw-justify-center tw-gap-4 tw-pt-4
            [&_*]:tw-text-sm [&_*]:lg:tw-text-xs [&_*]:tw-font-semibold
            [&_*]:tw-tracking-tighter [&_*]:tw-no-underline [&_*]:tw-uppercase
            [&_*]:tw-hover:tw-no-underline
            [&_*]:tw-rounded-full [&_*]:tw-min-w-36 
            [&_*]:tw-flex [&_*]:tw-justify-center
            `}
    >
      {props.children}
    </div>
  );
}

function Actions(props: { children: ReactNode }) {
  return <div className="tw-p-4">{props.children}</div>;
}

function TrackYourProgressActions(props: { children: ReactNode }) {
  return <div className="tw-p-4 tw-pt-12">{props.children}</div>;
}

function ActionLink(props: { children: ReactNode; to: string; replace?: boolean }) {
  return (
    <Link to={props.to} replace={props.replace || false}>
      {props.children}
    </Link>
  );
}

function ActionLinkSecondary(props: { children: ReactNode }) {
  return (
    <span
      className={`tw-bg-secondary tw-hover:tw-text-white tw-py-3 tw-text-white tw-border-solid tw-border-secondary`}
    >
      {props.children}
    </span>
  );
}

function ActionLinkCustom(props: { children: ReactNode; className: string }) {
  return <span className={`${props.className} tw-py-3`}>{props.children}</span>;
}

function ActionButton(props: { children: ReactNode; onClick: () => void; disabled?: boolean; className?: string }) {
  return (
    <button
      className={`tw-font-inter tw-border-0 tw-p-0 tw-cursor-pointer 
      tw-hover:tw-text-white tw-text-white tw-border-solid tw-rounded-full ${props.className || ''}`}
      onClick={props.onClick}
      disabled={props.disabled || false}
    >
      {props.children}
    </button>
  );
}

function ActionButtonPrimary(props: { children: ReactNode }) {
  return (
    <span className={`tw-font-inter tw-bg-primary tw-border-primary tw-p-3 tw-rounded-full`}>{props.children}</span>
  );
}

function ActionButtonSecondary(props: { children: ReactNode }) {
  return (
    <span className={`tw-font-inter tw-bg-secondary tw-border-secondary tw-p-3 tw-rounded-full`}>{props.children}</span>
  );
}

function ActionButtonCustom(props: { children: ReactNode; className: string }) {
  return <span className={`${props.className} tw-font-inter tw-p-3 tw-rounded-full`}>{props.children}</span>;
}

function Loader() {
  return (
    <div className="tw-flex tw-justify-center tw-w-full tw-p-8">
      <div className="loader tw-w-12 lg:tw-w-24"></div>
    </div>
  );
}

ContainerList.Item = ContainerListItem;

UploadBoxContainer.Title = ContainerTitle;
UploadBoxContainer.Text = ContainerText;
UploadBoxContainer.Action = ContainerAction;
UploadBoxContainer.List = ContainerList;

UploadBox.Container = UploadBoxContainer;
UploadBox.Image = UploadBoxImage;
Page.UploadBox = UploadBox;

TakePictureContainer.Action = TakePictureContainerAction;
TakePictureBox.Container = TakePictureContainer;
Page.TakePictureBox = TakePictureBox;

ActionLink.Secondary = ActionLinkSecondary;
ActionLink.Custom = ActionLinkCustom;
ActionButton.Primary = ActionButtonPrimary;
ActionButton.Secondary = ActionButtonSecondary;
ActionButton.Custom = ActionButtonCustom;

Page.Title = Title;
Page.Text = Text;
Page.ErrorText = ErrorText;
Page.Link = ActionLink;
Page.Button = ActionButton;
Page.Actions = Actions;
Page.Loader = Loader;
Page.DateText = DateText;
Page.TrackYourProgressActions = TrackYourProgressActions;
Page.PhotosBox = PhotosBox;

export default Page;
